import { Injectable } from "@angular/core";
import { asEnumerable } from "linq-es2015";
import { PollinationCategoryEnum } from "./pollination-category.service";
import { ConjunctionEnum } from "../models/conjunction.enum";

@Injectable()
export class PollinationStakeLabelService {
  constructor() { }

  public getGroupConjunction(groupIndex: number, conjunction: ConjunctionEnum, hasCategory: boolean): string {
    if (groupIndex === 0) {
      return "";
    }
    
    if (!hasCategory) {
      return conjunction === ConjunctionEnum.And ? "&" : "/";
    }
    
    return "";
  }

  public createStakeLabel(items: PollinationStakeLabelItemData[], pollinationCategoryId?: number, pollinationCategoryName?: string): string {
    const categoryId = pollinationCategoryId;

    switch (categoryId) {
      case PollinationCategoryEnum.ZygosityDependent:
        return 'Zygosity-dependent';
      case PollinationCategoryEnum.CrossingBlock:
        return 'Crossing block';
      case PollinationCategoryEnum.Standard:
        return this.createStandardStakeLabel(items, pollinationCategoryName || '');
      default:
        if (!pollinationCategoryId && !pollinationCategoryName) {
          return this.createStandardStakeLabel(items, '');
        }
        return '';
    }
  }

  private createStandardStakeLabel(items: PollinationStakeLabelItemData[], categoryName: string): string {
    const processedItems = items.map(i => {
      const processed = { ...i };

      switch (i.pollinationType) {
        case PollinationTypeEnum.Self:
          processed.truncatedPollinationType = "SLF";
          break;
        case PollinationTypeEnum.Sibling:
          processed.truncatedPollinationType = "SIB";
          break;
        case PollinationTypeEnum.CarryTo:
          processed.truncatedPollinationType = this.truncate(`XOT:${i.geName}`, 21);
          break;
        case PollinationTypeEnum.CarryFrom:
          processed.truncatedPollinationType = this.truncate(`XIN:${i.geName}`, 21);
          break;
        case PollinationTypeEnum.SelfSibling:
          processed.truncatedPollinationType = "(SLF/SIB)";
          break;
        case PollinationTypeEnum.None:
          processed.truncatedPollinationType = "None";
          break;
        default:
          processed.truncatedPollinationType = "";
          break;
      }
      return processed;
    });

    const validItems = processedItems.filter(i => i.truncatedPollinationType && i.truncatedPollinationType.trim() !== "");

    const groupedItems = asEnumerable(validItems)
      .GroupBy(g => g.groupIndex)
      .ToArray();

    const isLegacyRequest = categoryName === '';
    
    if (isLegacyRequest) {
      const fullStakeLabel = asEnumerable(groupedItems)
        .Select(g => {
          const firstItem = asEnumerable(g).First() as any;
          const groupConjunction = firstItem.groupConjunction;
          const itemLabels = asEnumerable(g)
            .OrderBy((i: any) => i.itemIndex)
            .Select((i: any) => `${i.itemConjunction}${i.truncatedPollinationType}`)
            .ToArray();

          const groupContent = itemLabels.join("");

          const needsParentheses = groupedItems.length > 1 || itemLabels.length > 1;

          return needsParentheses ?
            `${groupConjunction}(${groupContent})` :
            `${groupConjunction}${groupContent}`;
        })
        .ToArray()
        .join("");

      return this.truncate(fullStakeLabel, 60);
    }

    return this.createStandardCategoryLabel(groupedItems);
  }

  private createStandardCategoryLabel(groupedItems: any[]): string {
    const lines: string[] = [];

    const primaryGroup = groupedItems.find((g: any) => {
      const firstItem = asEnumerable(g).First() as any;
      return firstItem.groupIndex === 0;
    });

    const secondaryGroup = groupedItems.find((g: any) => {
      const firstItem = asEnumerable(g).First() as any;
      return firstItem.groupIndex === 1;
    });

    let primaryContent = "";
    let hasValidPrimaryInstructions = false;

    if (primaryGroup) {
      const primaryLabels = asEnumerable(primaryGroup)
        .OrderBy((item: any) => item.itemIndex)
        .Select((item: any) => `${item.itemConjunction}${item.truncatedPollinationType}`)
        .ToArray();

      primaryContent = primaryLabels.join("");
      hasValidPrimaryInstructions = primaryContent.trim() !== "";
    }

    let secondaryContent = "";
    let hasValidSecondaryInstructions = false;
    let primaryConjunction = "";

    if (secondaryGroup) {
      const secondaryLabels = asEnumerable(secondaryGroup)
        .OrderBy((item: any) => item.itemIndex)
        .Select((item: any) => `${item.itemConjunction}${item.truncatedPollinationType}`)
        .ToArray();

      secondaryContent = secondaryLabels.join("");
      hasValidSecondaryInstructions = secondaryContent.trim() !== "";

      if (hasValidPrimaryInstructions && hasValidSecondaryInstructions) {
        const firstSecondaryItem = asEnumerable(secondaryGroup).First() as any;
        const groupConjunction = firstSecondaryItem.groupConjunction;
        primaryConjunction = groupConjunction === "&" ? "&" : (groupConjunction === "/" ? "/" : "");
      }
    }

    if (hasValidPrimaryInstructions) {
      const needsParentheses = primaryGroup && asEnumerable(primaryGroup).Count() > 1;
      const formattedPrimaryContent = needsParentheses ? `(${primaryContent})` : primaryContent;
      lines.push(`Primary Standard: ${formattedPrimaryContent}${primaryConjunction}`);
    } else {
      lines.push(`Primary Standard:`);
    }

    if (hasValidSecondaryInstructions) {
      const needsParentheses = secondaryGroup && asEnumerable(secondaryGroup).Count() > 1;
      const formattedSecondaryContent = needsParentheses ? `(${secondaryContent})` : secondaryContent;
      lines.push(`Secondary Standard: ${formattedSecondaryContent}`);
    } else {
      lines.push(`Secondary Standard:`);
    }

    return lines.join("\n             ");
  }

  private truncate(input: string, maxLength: number): string {
    if (input === null || input.length <= maxLength)
      return input;

    return `${input.substring(0, maxLength - 1)}!`;
  }
}

export class PollinationStakeLabelItemData {
  groupIndex: number;
  groupConjunction: string;
  itemIndex: number;
  itemConjunction: string;
  pollinationType: PollinationTypeEnum;
  geName: string;
  truncatedPollinationType: string;
}

export enum PollinationTypeEnum {
  Self = 1,
  Sibling = 2,
  CarryTo = 3,
  CarryFrom = 4,
  SelfSibling = 5,
  None = 6
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-2/_work/98/s/src/app/shared/services/pollination-stake-label.service.ts